import { useQuery } from "@tanstack/react-query";
import { useEvent } from "../context/EventProvider";
import { fallbackOf } from "../settings/catalog";
import {
  useFlagOr,
  useLocale,
  useSettings,
  useT,
  useText,
} from "../settings/context";
import type { Answers } from "./answers";
import { standOf, type Me, type Recorded } from "./participant";
import { cleanSelection, euros, flatIds } from "./articles";
import { fetchAnswers, valueOf } from "./forms";
import { useForms } from "./formQueries";
import { ORDER_KEY } from "./order";
import {
  RECAP_FIELDS_KEY,
  candidates,
  formatFieldValue,
  infoFormKeys,
  readFieldRefs,
  selectedCandidates,
  servedForms,
} from "./recapFields";
import { useWordsOf } from "./useForm";
import { longSlot } from "./conferences";
import { longDay, selectedDaysOf } from "./dates";
import { inJourney } from "./thread";
import { useOffer } from "./useOffre";
import { useArticles, useResume } from "./useArticles";
import { useCompanions } from "./useCompanions";
import { useConferences } from "./useConferences";
import { useCompany } from "./useSociete";
import {
  useArticlesWord,
  useCompanyWord,
  useConferencesWord,
  useDatesWord,
  useParticipationWord,
  useTypologyWord,
} from "./wording";

/**
 * THE RECAP — a mirror of the funnel, on the confirmation.
 *
 * The prototype's `.recap` / `.rsec` / `.rrow`. It exists so a participant can
 * CHECK what was recorded rather than take a sentence's word for it: "your
 * registration is recorded" says nothing about which day, which profile, or which
 * address the badge will carry.
 *
 * ⚠️ IT SHOWS WHAT THE SERVER HOLDS, falling back to what was just answered. Built
 * from the navigation state alone it would mirror the form rather than the
 * registration — and the two part company the moment a write half-fails.
 *
 * ⚠️ A ROW WITH NOTHING IN IT DOES NOT APPEAR, the same rule as the footer. An
 * empty line under a heading reads as a broken page rather than as an absent
 * detail.
 *
 * It only reports what V2 actually collects today: who, how, which profile. Days,
 * conferences, companions and the amount arrive with their own steps — see the
 * note on the confirmation screen.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback: what the site says of itself follows the language being read.
 */

const LABELS: Record<string, string> = {
  onsite: "FO_MEETING_V2_REGISTRATION_ONSITE_LABEL",
  remote: "FO_MEETING_V2_REGISTRATION_REMOTE_LABEL",
  represented: "FO_MEETING_V2_REGISTRATION_REPRESENTED_LABEL",
  decline: "FO_MEETING_V2_REGISTRATION_DECLINE_LABEL",
};

function Row({ label, value }: { label: string; value: string }) {
  if (!value.trim()) return null;
  return (
    <div className="rrow">
      <span>{label}</span>
      <b>{value}</b>
    </div>
  );
}

export function Recap({
  answers,
  recorded,
  me,
}: {
  answers: Answers;
  recorded: Recorded | undefined;
  me: Me | undefined;
}) {
  const t = useT();
  const { value, typos, days } = useSettings();
  const offer = useOffer();
  // The client's own words, not mine — see `wording.ts`.
  const typologyWord = useTypologyWord();
  const participationWord = useParticipationWord();
  const companyWord = useCompanyWord();
  const datesWord = useDatesWord();
  const conferencesWord = useConferencesWord();
  const articlesWord = useArticlesWord();
  const companionsWord = useText(
    "FO_MEETING_V2_COMPANIONS_LABEL",
    fallbackOf("FO_MEETING_V2_COMPANIONS_LABEL"),
  );
  /* Défini ici plutôt qu'au milieu du corps : la billetterie en a besoin dès la
     lecture du catalogue, qui choisit le libellé français ou anglais. */
  const locale = useLocale();

  /**
   * ═══ CHAQUE LIGNE A SON INTERRUPTEUR ═══ (demandé le 2026-09-17)
   *
   * Allumés par défaut : la carte des événements déjà réglés ne bouge pas. Une ligne
   * allumée reste soumise à la règle de la carte — sans valeur, elle ne s'affiche pas.
   * Les sections à plusieurs lignes (conférences, commande, accompagnants,
   * représentation) s'éteignent d'un bloc, titre compris.
   */
  const show = {
    name: useFlagOr("FO_MEETING_V2_RECAP_NAME"),
    email: useFlagOr("FO_MEETING_V2_RECAP_EMAIL"),
    participation: useFlagOr("FO_MEETING_V2_RECAP_PARTICIPATION"),
    typology: useFlagOr("FO_MEETING_V2_RECAP_TYPOLOGY"),
    dates: useFlagOr("FO_MEETING_V2_RECAP_DATES"),
    company: useFlagOr("FO_MEETING_V2_RECAP_COMPANY"),
    reason: useFlagOr("FO_MEETING_V2_RECAP_REASON"),
    conferences: useFlagOr("FO_MEETING_V2_RECAP_CONFERENCES"),
    articles: useFlagOr("FO_MEETING_V2_RECAP_ARTICLES"),
    companions: useFlagOr("FO_MEETING_V2_RECAP_COMPANIONS"),
    representation: useFlagOr("FO_MEETING_V2_RECAP_REPRESENTATION"),
  };

  /**
   * ═══ LES CHAMPS DU FORMULAIRE, DONT LES DONNÉES ÉTENDUES ═══
   *
   * Ceux que l'organisateur a cochés (`FO_MEETING_V2_RECAP_FIELDS`), parmi les
   * formulaires d'informations servis dans le parcours — voir `recapFields.ts`.
   *
   * ⚠️ LA MÊME REQUÊTE QUE L'ÉTAPE INFORMATIONS, même clé de cache : elle a déjà été
   * lue en remplissant le formulaire, et elle ne part ici que si au moins un champ est
   * coché — sur un événement qui n'en coche aucun, rien n'est demandé.
   */
  const event = useEvent();
  const forms = useForms();
  const served = servedForms(infoFormKeys(value(ORDER_KEY)), value, forms);
  const wordsByForm = useWordsOf(served);
  const fieldRefs = readFieldRefs(value(RECAP_FIELDS_KEY));
  const fields = selectedCandidates(fieldRefs, candidates(served));
  const formAnswers = useQuery({
    queryKey: ["form-answers", event?.domainId, event?.eventId, me?.id],
    queryFn: ({ signal }) =>
      fetchAnswers(
        event!.domainId,
        { eventId: event!.eventId, userId: me!.id },
        signal,
      ),
    enabled: fields.length > 0 && !!event && typeof me?.id === "number",
    staleTime: Infinity,
    retry: false,
  });
  const yes = t("YES", "Oui");
  const fieldRows = formAnswers.data
    ? fields.map(({ field, form }) => ({
        key: `${field.model}:${field.key}`,
        label:
          (wordsByForm.get(form.id)?.field(field) ?? field.label).trim() ||
          field.key,
        value: formatFieldValue(
          field,
          valueOf(formAnswers.data, field),
          wordsByForm.get(form.id),
          yes,
        ),
      }))
    : [];

  /**
   * ⚠️ DEMANDÉE SEULEMENT SI L'ÉTAPE EXISTE. Le rattachement est un identifiant de
   * ligne, pas un nom : il faut aller chercher la société pour l'écrire ici. Sans
   * cette garde, chaque confirmation de chaque événement — l'immense majorité n'ayant
   * pas de sociétés — paierait une requête pour une ligne qui ne s'afficherait jamais.
   *
   * ⚠️ « L'ÉTAPE EXISTE » SE LIT PORTE MISE À PART (`inJourney`), pour ces trois gardes.
   * Inscriptions closes, le fil actif ne garde plus que la participation et la
   * confirmation : lu dessus, ce récapitulatif effaçait la commande qu'il restait à
   * payer juste au-dessus du bouton « Payer » (tâche #332).
   */
  const { attached } = useCompany(
    inJourney(value, "/registration/company", offer),
  );

  /* Même garde, même raison : sans étape conférences dans le parcours, la section
     n'aurait rien à montrer et la requête serait payée pour rien. */
  const { selected } = useConferences(
    inJourney(value, "/registration/conferences", offer),
  );

  /* Et encore la même, pour la billetterie : deux requêtes de plus sur chaque
     confirmation de chaque événement, alors que presque aucun ne vend quoi que ce
     soit. */
  const ticketing = inJourney(value, "/registration/articles", offer);
  const { articles, saved } = useArticles(ticketing, locale);
  /* Même garde que les conférences et la billetterie : sans le module, la section
     n'aurait rien à montrer et la requête serait payée pour rien. */
  const { companions } = useCompanions(
    useFlagOr("FO_MEETING_V2_COMPANIONS_SHOW"),
  );

  const answer = answers.participation ?? recorded?.answer;

  /**
   * LES JOURS RETENUS — par la MÊME règle que celle qui les écrit.
   *
   * ⚠️ ET C'EST TOUTE LA RAISON DE LES CALCULER PLUTÔT QUE DE LES LIRE. Sans étape
   * dates — un événement d'un seul jour, ou l'étape retirée — le participant est
   * inscrit à TOUS les jours sans avoir rien coché : la ligne le dit, alors qu'une
   * lecture des seules réponses l'aurait laissée vide. Deux règles séparées se
   * seraient contredites ici même, à l'écran qui existe pour vérifier.
   *
   * Rien à afficher pour qui ne vient pas : `joursRetenus` rend une liste vide, et une
   * ligne vide ne s'affiche pas.
   */
  const selectedDays = selectedDaysOf({
    coming: answer === "onsite" || answer === "remote",
    ...(answers.days ? { chosen: answers.days } : {}),
    ...(recorded?.days ? { saved: recorded.days } : {}),
    all: days.map((d) => d.id),
  });
  /**
   * ⚠️ RIEN SANS L'ÉTAPE, MÊME SI LE CACHE A ENCORE QUELQUE CHOSE. Les deux requêtes
   * ci-dessus sont éteintes quand la billetterie n'est pas du parcours, mais leur cache
   * garde la réponse d'une visite d'avant — et il en existe une : quelqu'un qui vient de
   * changer pour une typologie sans billetterie a vu sa commande à la confirmation
   * précédente. Mesuré : la section affichait cette commande-là, chiffrée, alors que
   * l'écriture venait de la vider (la règle vit au serveur,
   * `api-v1/api/helpers/cartOnTypoChange.js`). Le fil d'Ariane, à
   * côté, ne servait déjà plus l'étape.
   */
  const chosenOrder = ticketing ? (answers.articles ?? saved ?? {}) : {};
  /**
   * ⚠️ NETTOYÉE PAR LES JOURS RETENUS, comme à l'écriture (`cleanSelection`, bug 1 de la
   * chasse du 2026-10-20). Le billet d'un jour qu'on vient de décocher reste dans l'état de
   * navigation (et dans la commande enregistrée jusqu'à la prochaine écriture) : le serveur
   * l'écarte à l'enregistrement, mais ce récapitulatif le listait et le chiffrait quand
   * même — l'écran qui existe pour vérifier affichait une commande que personne ne recevrait.
   * Même règle que `saveParticipation` : sans jours déclarés par l'événement, aucun filtre.
   */
  const order =
    days.length > 0
      ? cleanSelection(chosenOrder, { selectedDays })
      : chosenOrder;
  const mine = flatIds(order, articles);
  const { resume } = useResume(mine, ticketing);

  // Read unconditionally — a hook cannot hang off which answer was given.
  const onsite = useText(LABELS.onsite!, fallbackOf(LABELS.onsite!));
  const remote = useText(LABELS.remote!, fallbackOf(LABELS.remote!));
  const represented = useText(
    LABELS.represented!,
    fallbackOf(LABELS.represented!),
  );
  const decline = useText(LABELS.decline!, fallbackOf(LABELS.decline!));

  const said =
    answer === "onsite"
      ? onsite
      : answer === "remote"
        ? remote
        : answer === "represented"
          ? represented
          : answer === "decline"
            ? decline
            : "";

  const typoId = answers.typoId ?? recorded?.typoId;
  const profile = typos.find((t) => t.id === typoId)?.name ?? "";
  const reason = answers.reason ?? recorded?.reason ?? "";
  /* ⚠️ COMME LES AUTRES LIGNES : ce qui vient d'être saisi, sinon ce que le serveur
     détient. Lu dans le seul état de navigation, le remplaçant disparaissait du récap dès
     qu'on y revenait par un lien ou un onglet neuf. Voir `standOf`. */
  const stand = answers.stand ?? standOf(recorded?.extended);
  const standName = [stand?.first, stand?.last].filter(Boolean).join(" ");

  const name = [me?.firstName, me?.lastName].filter(Boolean).join(" ");

  const when = days
    .filter((d) => selectedDays.includes(d.id))
    .map((d) => longDay(d.day, locale))
    .join(" · ");

  /**
   * LA COMMANDE, UNE LIGNE PAR ARTICLE ET PAR JOUR.
   *
   * ⚠️ ET NON UNE LIGNE PAR ARTICLE. Le même pass retenu sur deux journées est ce qui
   * tient lieu de QUANTITÉ chez le produit (voir `funnel/articles.ts`) : regroupé, il
   * s'afficherait une fois et le total du dessous paraîtrait double sans raison.
   *
   * ⚠️ UN ARTICLE QUE LE CATALOGUE NE CONNAÎT PLUS EST TU. Le back office peut en avoir
   * supprimé un depuis ; écrire son identifiant faute de nom donnerait une ligne
   * « 1150 » au milieu d'une facture.
   */
  const articleLines = Object.entries(order).flatMap(([day, ids]) =>
    ids.flatMap((id) => {
      const article = articles.find((a) => a.id === id);
      if (!article) return [];
      const dayText = days.find((d) => d.id === Number(day));
      return [
        {
          key: `${day}-${id}`,
          label: article.label,
          // Le niveau « hors jour » ne se rattache à aucune journée : c'est ce qui se
          // vend pour l'événement entier, et le dire vaut mieux qu'une valeur vide.
          when: dayText
            ? longDay(dayText.day, locale)
            : t("WHOLE_EVENT", "Tout l'événement"),
        },
      ];
    }),
  );

  /**
   * LES BILLETS DES ACCOMPAGNANTS — ce que le serveur compte en plus de la sélection.
   *
   * ⚠️ LE TOTAL LES COMPTAIT, LA LISTE NON (bug #201). Le serveur facture au porteur la
   * commande de ses accompagnants, mais les lignes du dessus ne viennent que de SA
   * sélection : « Total 338 € » sous un seul billet à 169 €. Ce qu'il chiffre en plus de
   * ce qu'on lui a envoyé, article par article, est donc à eux.
   *
   * ⚠️ PAR DIFFÉRENCE, ET NON EN RELISANT LEUR LISTE : il regroupe par article, donc un
   * même pass pris par le porteur et par son accompagnant revient en UNE ligne de
   * quantité 2, dont une seule est déjà écrite au-dessus.
   */
  const companionLines = (resume?.lines ?? []).flatMap((line) => {
    const extra = line.quantity - mine.filter((id) => id === line.id).length;
    if (extra <= 0) return [];
    const label = articles.find((a) => a.id === line.id)?.label ?? line.label;
    return [
      {
        key: `companion-${line.id}`,
        label: extra > 1 ? `${label} × ${extra}` : label,
        when: t("COMPANION", "Accompagnant"),
      },
    ];
  });
  const ticketLines = [...articleLines, ...companionLines];

  return (
    /**
     * `recap--grid` : sur un site large, les lignes se rangent sur DEUX colonnes par
     * section (voir `desktop.css`). Demandé le 2026-09-17 : une ligne par information,
     * la carte occupait tout l'écran dès qu'on cochait quelques champs. Chaque section
     * est un groupe (`.rgrp`) pour que la grille reparte à gauche sous son titre.
     */
    <div className="recap recap--grid" data-cfg="confirmationRecap">
      {/* ⚠️ THE PROTOTYPE'S GROUPING: sections are SUBJECTS, not fields. Who you are
          and how you take part belong to the same subject — "Participation" is a ROW
          under "Participant" there, not a heading of its own. Split into two
          sections it printed the event's word as a heading and again as a label one
          line below, and the card read as twice as long as it is. */}
      <div className="rgrp">
        <div className="rsec">{t("PARTICIPANT", 'Participant')}</div>
        {show.name && <Row label={t("FULL_NAME", "Nom")} value={name} />}
        {show.email && (
          <Row label={t("EMAIL_ADDRESS", "Adresse email")} value={me?.email ?? ""} />
        )}
        {show.participation && <Row label={participationWord} value={said} />}
        {show.typology && <Row label={typologyWord} value={profile} />}
        {show.dates && <Row label={datesWord} value={when} />}
        {/* La société est une LIGNE du participant, pas une section à elle : c'est un
            attribut de la personne qui vient, au même titre que son profil — la règle
            de regroupement du prototype, qui réserve les sections aux autres gens. */}
        {show.company && <Row label={companyWord} value={attached?.name ?? ""} />}
        {/* Les champs du formulaire cochés par l'organisateur : des attributs de la
            personne, donc des LIGNES du participant, après la société. */}
        {fieldRows.map((r) => (
          <Row key={r.key} label={r.label} value={r.value} />
        ))}
        {show.reason && answer === "decline" && (
          <Row label={t("DECLINE_REASON", "Motif")} value={reason} />
        )}
      </div>

      {/* ⚠️ UNE SECTION, PAS UNE LIGNE — et c'est le seul cas où la règle du prototype
          (les sections sont pour les AUTRES gens) admet une exception justifiée : il y
          en a PLUSIEURS, chacune avec son horaire. Aplaties en une ligne, deux
          conférences et leurs créneaux tiendraient sur une valeur illisible, alors que
          l'horaire est précisément ce qu'on revient vérifier. */}
      {show.conferences && selected.length > 0 && (
        <div className="rgrp">
          <div className="rsec">{conferencesWord}</div>
          {selected.map((conference) => {
            const slot = conference.slots.find((c) => c.selected);
            return (
              <Row
                key={conference.id}
                label={conference.title}
                value={
                  slot ? longSlot(slot.start, slot.end, locale) : ""
                }
              />
            );
          })}
        </div>
      )}

      {/**
       * ⚠️ UNE SECTION, ET LE MONTANT EN DERNIÈRE LIGNE. Comme les conférences, il y a
       * PLUSIEURS lignes — un article par jour retenu — donc une section et non une
       * ligne aplatie. Ce qu'on revient vérifier ici, c'est ce qu'on a commandé et ce
       * qu'il reste à payer.
       *
       * ⚠️ CE QUI A ÉTÉ RÉPONDU D'ABORD, comme partout ailleurs dans ce récapitulatif :
       * la sélection vient d'être écrite, mais la requête qui la relit peut encore être
       * en vol — afficher l'enregistré seul laisserait la commande vide au moment
       * précis où on vient de la passer.
       */}
      {show.articles && ticketLines.length > 0 && (
        <div className="rgrp">
          <div className="rsec">{articlesWord}</div>
          {ticketLines.map((line) => (
            <Row key={line.key} label={line.label} value={line.when} />
          ))}
          {/**
           * ⚠️ LE TOTAL DE LA COMMANDE, ET SURTOUT PAS LE RESTE À RÉGLER. Deux montants
           * pour la même chose se contredisaient à l'écran : ce récapitulatif annonçait
           * « Reste à régler 110,00 € » pendant que le bloc de paiement, deux
           * centimètres plus bas, disait « 100,00 € HT ». Les deux avaient raison — le
           * second suit le régime de facturation choisi par le payeur, qui décide de la
           * TVA — mais celui qui lit voit deux nombres et ne sait plus lequel il va
           * payer.
           *
           * La règle qui en sort : le SOLDE ne se dit qu'à un seul endroit, celui où on
           * le règle. Ici on dit ce qui a été COMMANDÉ.
           *
           * ⚠️ ET LE COMPTE SE POSE EN ENTIER, COMME SUR « MA COMMANDE » ET LA FACTURE :
           * sous-total hors taxes, déductions, TVA, total TTC, puis le déjà-réglé. Sans
           * la TVA ni le total TTC, la carte passait de « 338,00 € HT » à « − 405,60 €
           * TTC » et concluait « 0,00 € » : une soustraction que personne ne pouvait
           * refaire (bug #201).
           */}
          {resume && resume.subtotalExclTax > 0 && (
            <Row
              label={t("SUBTOTAL", "Sous-total")}
              value={t("AMOUNT_EXCL_TAX", "$1 HT", [euros(resume.subtotalExclTax, locale)])}
            />
          )}

          {/**
           * ⚠️ TOUTES LES DÉDUCTIONS, OU AUCUNE — UN COMPTE À MOITIÉ POSÉ EST PIRE QUE
           * PAS DE COMPTE. Cet écran n'affichait que la commande et le déjà-réglé : sur
           * un compte réel, il montrait « 2 650 € » puis « − 2 290 € » et taisait les
           * 360 € de remise. Le lecteur fait la soustraction qu'on lui met sous les
           * yeux, trouve 360 € restants, et cherche un bouton pour payer une somme qu'il
           * ne doit pas. Le serveur, lui, comptait juste : 2 650 − 360 − 2 290 = 0.
           *
           * Les quatre lignes du serveur sont donc servies, et seulement celles qui
           * portent quelque chose : une ligne « Offert 0,00 € » n'apprendrait rien et
           * allongerait la carte.
           */}
          {resume && resume.freeExclTax > 0 && (
            <Row
              label={t("FREE_OF_CHARGE", "Offert")}
              value={`− ${t("AMOUNT_EXCL_TAX", "$1 HT", [euros(resume.freeExclTax, locale)])}`}
            />
          )}

          {resume && resume.discountExclTax > 0 && (
            <Row
              label={t("DISCOUNT", "Remise")}
              value={`− ${t("AMOUNT_EXCL_TAX", "$1 HT", [euros(resume.discountExclTax, locale)])}`}
            />
          )}

          {resume?.vatLines.map((line) => (
            <Row
              key={line.rate}
              label={t("VAT_RATE", "TVA $1 %", [line.rate])}
              value={euros(line.amount, locale)}
            />
          ))}

          {resume && resume.subtotalExclTax > 0 && (
            <Row
              label={t("ORDER_TOTAL", "Total de la commande")}
              value={t("AMOUNT_INCL_TAX", "$1 TTC", [euros(resume.orderInclTax, locale)])}
            />
          )}

          {/* ⚠️ EN TOUTES TAXES, comme sur l'écran de billetterie : c'est la somme que
              la personne a sortie. Les lignes au-dessus de la TVA restent hors taxes
              parce qu'elles déduisent des articles, qui se comptent ainsi. */}
          {resume && resume.alreadyPaidInclTax > 0 && (
            <Row
              label={t("ALREADY_PAID", "Déjà réglé")}
              value={`− ${t("AMOUNT_INCL_TAX", "$1 TTC", [euros(resume.alreadyPaidInclTax, locale)])}`}
            />
          )}

          {/**
           * ⚠️ LA CONCLUSION N'EST TIRÉE QUE QUAND IL N'Y A PLUS RIEN À RÉGLER, et c'est
           * la règle « un seul endroit dit le solde » : s'il reste quelque chose, c'est
           * le bloc de PAIEMENT, juste en dessous, qui l'annonce — avec le montant du
           * régime fiscal choisi, que ce récapitulatif ne connaît pas. Répété ici en
           * hors-taxes, il donnerait deux nombres pour la même dette.
           *
           * Mais quand le compte est soldé, ce bloc-là ne s'affiche pas : sans cette
           * ligne, la carte s'arrêterait sur une soustraction que personne ne conclut.
           */}
          {resume && !resume.toPay && (
            <Row label={t("LEFT_TO_PAY", "Reste à régler")} value={euros(0, locale)} />
          )}
        </div>
      )}

      {/**
       * ⚠️ UNE SECTION, PARCE QUE CE SONT D'AUTRES GENS — la règle de regroupement du
       * prototype, celle qui garde la société en simple LIGNE du participant et réserve
       * les sections aux personnes. Un accompagnant n'est pas un attribut de
       * l'inscription : c'est un compte à lui, avec sa participation et son badge.
       *
       * ⚠️ LE RANG À GAUCHE, LE NOM EN VALEUR — la disposition du prototype
       * (`row('Badge ' + (i + 1), nomAccomp(a))`), et j'avais commencé par l'inverse.
       * Deux raisons, l'une esthétique et l'autre pas :
       *
       *   — le NOM est l'information, et dans cette carte l'information est à droite,
       *     en gras ; mise à gauche elle prenait la place grise des intitulés ;
       *   — surtout, `Row` masque une ligne dont la VALEUR est vide. Avec le nom comme
       *     intitulé et l'adresse comme valeur, tout accompagnant sans compte — le cas
       *     le plus fréquent — disparaissait, et la section s'affichait avec son titre
       *     au-dessus du vide. Mesuré à l'écran sur un compte réel.
       *
       * « Badge N » plutôt qu'un numéro nu : c'est ce que la personne reçoit, et ce qui
       * distingue les lignes entre elles quand deux accompagnants portent le même nom.
       */}
      {show.companions && companions.length > 0 && (
        <div className="rgrp">
          <div className="rsec">{companionsWord}</div>
          {companions.map((a, i) => (
            <Row key={a.id} label={t("BADGE_N", "Badge $1", [i + 1])} value={a.name} />
          ))}
        </div>
      )}

      {/* Its own subject, and only when there is one — the prototype gives the
          companions a section for the same reason: they are other people. */}
      {show.representation && answer === "represented" && (standName || stand?.email) && (
        <div className="rgrp">
          <div className="rsec">{t("REPRESENTATION", 'Représentation')}</div>
          <Row label={t("FULL_NAME", "Nom")} value={standName} />
          <Row label={t("EMAIL_ADDRESS", "Adresse email")} value={stand?.email ?? ""} />
        </div>
      )}
    </div>
  );
}
